import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  PageHeader,
  Drawer,
  BadgePill,
  EmptyState,
} from '@/components/campus';
import {
  Megaphone,
  Plus,
  Loader2,
  Eye,
  Users,
  Clock,
  CheckCircle,
  Paperclip,
  MoreHorizontal,
  Trash2,
  Edit,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { AIAnnouncementGenerator } from '@/components/ai';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Audience options (matching database audience_enum)
const AUDIENCE_OPTIONS = [
  { value: 'all_members', label: 'Everyone' },
  { value: 'active_members', label: 'Active Members' },
  { value: 'officers', label: 'Officers Only' },
  { value: 'pledges', label: 'Pledges' },
  { value: 'alumni', label: 'Alumni' },
];

// Format helpers
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

// ==================== CREATE ANNOUNCEMENT FORM ====================
const CreateAnnouncementForm = ({ onSubmit, onCancel, isLoading, initialData, organizationId }) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [body, setBody] = useState(initialData?.body || '');
  const [audience, setAudience] = useState(initialData?.audience || 'all_members');
  const [attachmentUrl, setAttachmentUrl] = useState(initialData?.attachment_url || '');
  const [errors, setErrors] = useState({});
  const [showAIWriter, setShowAIWriter] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!body.trim()) newErrors.body = 'Message is required';
    if (!audience) newErrors.audience = 'Audience is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      title,
      body,
      audience,
      attachment_url: attachmentUrl || null,
    });
  };

  const handleAIGenerated = (content) => {
    setBody(content);
    setShowAIWriter(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Title */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Title *</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Important Update for All Members"
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="announcement-title-input"
        />
        {errors.title && <p className="text-signal-red text-xs">{errors.title}</p>}
      </div>

      {/* Audience */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Audience *</Label>
        <Select value={audience} onValueChange={setAudience}>
          <SelectTrigger className="bg-obsidian border-white/[0.08] text-cloud-white">
            <SelectValue placeholder="Select audience" />
          </SelectTrigger>
          <SelectContent className="bg-graphite border-white/[0.08]">
            {AUDIENCE_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value} className="text-cloud-white">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.audience && <p className="text-signal-red text-xs">{errors.audience}</p>}
      </div>

      {/* Body */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-soft-silver">Message *</Label>
          {!showAIWriter && organizationId && (
            <button
              type="button"
              onClick={() => setShowAIWriter(true)}
              className="text-xs text-electric-ice-blue hover:text-electric-ice-blue/80 flex items-center gap-1"
              data-testid="ai-writer-toggle"
            >
              <Megaphone className="w-3 h-3" />
              Write with AI
            </button>
          )}
        </div>
        
        {/* AI Writer Panel */}
        {showAIWriter && organizationId && (
          <AIAnnouncementGenerator
            organizationId={organizationId}
            audience={audience}
            onGenerated={handleAIGenerated}
            onClose={() => setShowAIWriter(false)}
          />
        )}
        
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write your announcement message here..."
          rows={6}
          className="bg-obsidian border-white/[0.08] text-cloud-white resize-none"
          data-testid="announcement-body-input"
        />
        {errors.body && <p className="text-signal-red text-xs">{errors.body}</p>}
      </div>

      {/* Attachment URL (optional) */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Attachment URL (optional)</Label>
        <Input
          value={attachmentUrl}
          onChange={(e) => setAttachmentUrl(e.target.value)}
          placeholder="https://..."
          className="bg-obsidian border-white/[0.08] text-cloud-white"
        />
        <p className="text-muted-text text-xs">Link to a document, image, or external resource</p>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 border-white/[0.08] text-soft-silver hover:text-cloud-white"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
          disabled={isLoading}
          data-testid="create-announcement-submit-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {initialData ? 'Saving...' : 'Posting...'}
            </>
          ) : (initialData ? 'Save Changes' : 'Post Announcement')}
        </Button>
      </div>
    </form>
  );
};

// ==================== ANNOUNCEMENT CARD ====================
const AnnouncementCard = ({ announcement, onClick, isAdmin, onEdit, onDelete }) => {
  const audienceLabel = AUDIENCE_OPTIONS.find(a => a.value === announcement.audience)?.label || announcement.audience;

  return (
    <div
      onClick={onClick}
      className={cn(
        "p-5 rounded-xl border cursor-pointer transition-all",
        announcement.is_read 
          ? "bg-graphite/30 border-white/[0.04] hover:border-white/[0.08]"
          : "bg-graphite/50 border-electric-ice-blue/30 hover:border-electric-ice-blue/50"
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {!announcement.is_read && (
              <span className="w-2 h-2 rounded-full bg-electric-ice-blue animate-pulse" />
            )}
            <h3 className="font-semibold text-cloud-white truncate">{announcement.title}</h3>
          </div>
          <p className="text-soft-silver text-sm line-clamp-2 mb-3">{announcement.body}</p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-text">
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {audienceLabel}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDate(announcement.created_at)}
            </span>
            {announcement.attachment_url && (
              <span className="flex items-center gap-1 text-electric-ice-blue">
                <Paperclip className="w-3 h-3" />
                Attachment
              </span>
            )}
          </div>
        </div>
        
        {isAdmin && (
          <div className="flex items-start gap-2 ml-4">
            {announcement.read_stats && (
              <div className="text-right">
                <p className="text-xs text-soft-silver">Read</p>
                <p className="text-sm font-medium text-electric-ice-blue">
                  {announcement.read_stats.read_count}/{announcement.read_stats.total_audience}
                </p>
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="w-4 h-4 text-muted-text" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-graphite border-white/[0.08]">
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onEdit(announcement); }}
                  className="text-cloud-white hover:bg-white/[0.05]"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); onDelete(announcement); }}
                  className="text-signal-red hover:bg-white/[0.05]"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ANNOUNCEMENT DETAIL VIEW ====================
const AnnouncementDetailView = ({ announcement, stats, isAdmin }) => {
  if (!announcement) return null;

  const audienceLabel = AUDIENCE_OPTIONS.find(a => a.value === announcement.audience)?.label || announcement.audience;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-cloud-white">{announcement.title}</h2>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-soft-silver">
          <span>By {announcement.created_by_name}</span>
          <span>•</span>
          <span>{formatDate(announcement.created_at)}</span>
          <BadgePill variant="default">{audienceLabel}</BadgePill>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
        <p className="text-cloud-white whitespace-pre-wrap">{announcement.body}</p>
      </div>

      {/* Attachment */}
      {announcement.attachment_url && (
        <Button
          onClick={() => window.open(announcement.attachment_url, '_blank')}
          variant="outline"
          className="w-full border-white/[0.08] text-cloud-white"
        >
          <Paperclip className="w-4 h-4 mr-2" />
          View Attachment
        </Button>
      )}

      {/* Read Stats (Admin) */}
      {isAdmin && stats && (
        <div className="space-y-4 pt-4 border-t border-white/[0.06]">
          <h4 className="font-medium text-soft-silver">Read Statistics</h4>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-cool-mint/10 rounded-lg text-center">
              <p className="text-cool-mint text-xl font-semibold">{stats.read_count}</p>
              <p className="text-soft-silver text-xs">Read</p>
            </div>
            <div className="p-3 bg-soft-amber/10 rounded-lg text-center">
              <p className="text-soft-amber text-xl font-semibold">{stats.unread_count}</p>
              <p className="text-soft-silver text-xs">Unread</p>
            </div>
            <div className="p-3 bg-electric-ice-blue/10 rounded-lg text-center">
              <p className="text-electric-ice-blue text-xl font-semibold">{stats.read_rate}%</p>
              <p className="text-soft-silver text-xs">Rate</p>
            </div>
          </div>

          {/* Readers List */}
          {stats.readers && stats.readers.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-sm text-soft-silver">Who has read ({stats.readers.length})</h5>
              <div className="max-h-[200px] overflow-y-auto space-y-2">
                {stats.readers.map(reader => (
                  <div
                    key={reader.user_id}
                    className="flex items-center justify-between p-2 bg-obsidian/30 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-cool-mint" />
                      <span className="text-cloud-white text-sm">{reader.name}</span>
                    </div>
                    <span className="text-muted-text text-xs">{formatDate(reader.read_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN ANNOUNCEMENTS PAGE ====================
export const Announcements = () => {
  const { organization, session } = useAuth();
  const { hasOrgRole } = usePermissions();
  const isAdmin = hasOrgRole(['owner', 'admin']);

  // State
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [announcementStats, setAnnouncementStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/announcements/list/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // Create/Update announcement
  const handleSubmitAnnouncement = async (data) => {
    setIsSubmitting(true);

    try {
      const url = editingAnnouncement 
        ? `${API_URL}/api/announcements/${editingAnnouncement.id}`
        : `${API_URL}/api/announcements`;
      
      const method = editingAnnouncement ? 'PUT' : 'POST';
      
      const body = editingAnnouncement 
        ? data 
        : { ...data, organization_id: organization.id };

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success(editingAnnouncement ? 'Announcement updated!' : 'Announcement posted!');
        setIsCreateOpen(false);
        setEditingAnnouncement(null);
        fetchAnnouncements();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save announcement');
      }
    } catch (error) {
      toast.error('Failed to save announcement');
    } finally {
      setIsSubmitting(false);
    }
  };

  // View announcement detail
  const handleViewAnnouncement = async (announcement) => {
    try {
      const res = await fetch(`${API_URL}/api/announcements/${announcement.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSelectedAnnouncement(data.announcement);
        setIsDetailOpen(true);

        // Mark as read in local state
        setAnnouncements(prev => prev.map(a => 
          a.id === announcement.id ? { ...a, is_read: true } : a
        ));

        // Fetch stats for admins
        if (isAdmin) {
          const statsRes = await fetch(`${API_URL}/api/announcements/${announcement.id}/stats`, {
            headers: { Authorization: `Bearer ${session.access_token}` }
          });
          if (statsRes.ok) {
            const statsData = await statsRes.json();
            setAnnouncementStats(statsData);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch announcement:', error);
    }
  };

  // Edit announcement
  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setIsCreateOpen(true);
  };

  // Delete announcement
  const handleDeleteAnnouncement = async (announcement) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const res = await fetch(`${API_URL}/api/announcements/${announcement.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Announcement deleted');
        fetchAnnouncements();
      } else {
        toast.error('Failed to delete announcement');
      }
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  // Unread count
  const unreadCount = announcements.filter(a => !a.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <PageHeader
        title="Announcements"
        subtitle={unreadCount > 0 ? `${unreadCount} unread` : 'Stay updated with your organization'}
        actions={
          isAdmin && (
            <Button
              onClick={() => {
                setEditingAnnouncement(null);
                setIsCreateOpen(true);
              }}
              className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
              data-testid="create-announcement-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Announcement
            </Button>
          )
        }
      />

      {/* Announcements Feed */}
      {announcements.length === 0 ? (
        <EmptyState
          icon="megaphone"
          title="No announcements"
          description={isAdmin ? "Post your first announcement to keep your members informed." : "No announcements yet."}
          actionLabel={isAdmin ? "Post Announcement" : undefined}
          onAction={isAdmin ? () => setIsCreateOpen(true) : undefined}
        />
      ) : (
        <div className="space-y-4">
          {announcements.map(announcement => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              onClick={() => handleViewAnnouncement(announcement)}
              isAdmin={isAdmin}
              onEdit={handleEditAnnouncement}
              onDelete={handleDeleteAnnouncement}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Drawer */}
      <Drawer
        isOpen={isCreateOpen}
        onClose={() => {
          setIsCreateOpen(false);
          setEditingAnnouncement(null);
        }}
        title={editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
        width="md"
      >
        <CreateAnnouncementForm
          onSubmit={handleSubmitAnnouncement}
          onCancel={() => {
            setIsCreateOpen(false);
            setEditingAnnouncement(null);
          }}
          isLoading={isSubmitting}
          initialData={editingAnnouncement}
          organizationId={organization?.id}
        />
      </Drawer>

      {/* Detail Drawer */}
      <Drawer
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedAnnouncement(null);
          setAnnouncementStats(null);
        }}
        title="Announcement"
        width="lg"
      >
        <AnnouncementDetailView
          announcement={selectedAnnouncement}
          stats={announcementStats}
          isAdmin={isAdmin}
        />
      </Drawer>
    </div>
  );
};

export default Announcements;
